import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import apiFetch from "../utils/apiFetch";
import UserContext from "../hooks/UserContext";

const FIELDS = [
  { key: "name", label: "Номи *", placeholder: "Тармоқ номи" },
  { key: "voltage", label: "Кучланиш (кВ)", placeholder: "110" },
  { key: "length", label: "Узунлик (км)", placeholder: "25.5" },
  { key: "span", label: "Оралиқ", placeholder: "" },
  { key: "start_year", label: "Ишга тушган йили", placeholder: "1985" },
  { key: "wire_type", label: "Сим тури", placeholder: "" },
  { key: "wire_protection_type", label: "Ҳимоя сими тури", placeholder: "" },
  { key: "optical_wire_type", label: "Оптик сим тури", placeholder: "" },
  { key: "poles_quantity", label: "Опоралар сони", placeholder: "" },
  { key: "intersections", label: "Кесишмалар", placeholder: "" },
];

const CURRENT_FIELDS = [
  { key: "conductor_allowed_nominal_current_a", label: "Ўтказгичнинг рухсат этилган номинал токи (A)", placeholder: "630", type: "number", min: "0", step: "any", inputMode: "decimal" },
  { key: "network_max_load_current_a", label: "Тармоқнинг максимал юклама токи, факт бўйича (A)", placeholder: "420", type: "number", min: "0", step: "any", inputMode: "decimal" },
];

const PRIMARY_FIELDS = FIELDS.slice(0, 5);
const TECH_FIELDS = FIELDS.slice(5);

export default function NetworkCreate() {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const isGlobal = user?.is_global_access;

  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [cities, setCities] = useState([]);
  const [cityId, setCityId] = useState("");
  const [cityLoading, setCityLoading] = useState(false);
  const [cityError, setCityError] = useState("");

  useEffect(() => {
    if (!isGlobal) return;
    let alive = true;

    async function loadCities() {
      setCityLoading(true);
      setCityError("");
      try {
        const res = await apiFetch("/api/city");
        if (!res.ok) throw new Error("CITY_LOAD_FAILED");
        const data = await res.json();
        if (alive) setCities(data);
      } catch (e) {
        console.error(e);
        if (alive) setCityError("Филиаллар рўйхати юкланмади.");
      } finally {
        if (alive) setCityLoading(false);
      }
    }

    loadCities();
    return () => {
      alive = false;
    };
  }, [isGlobal]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name?.trim()) return setError("Номи киритилиши шарт");
    if (isGlobal && !cityId) return setError("Филиални танланг");
    setSaving(true);
    setError("");
    try {
      const body = { ...form };
      if (isGlobal) body.cityId = Number(cityId);
      const res = await apiFetch("/api/network-management", { method: "POST", body });
      if (!res.ok) throw new Error((await res.json()).message);
      const net = await res.json();
      navigate(`/networks/${net.id}`);
    } catch (err) {
      setError(err.message || "Хатолик юз берди");
    } finally {
      setSaving(false);
    }
  }

  const renderField = (f) => (
    <div key={f.key} className={f.key === "intersections" ? "sm-field sm-form__full" : "sm-field"}>
      <label className="sm-field__label">{f.label}</label>
      <input
        className="sm-field__input"
        type={f.type || "text"}
        min={f.min}
        step={f.step}
        inputMode={f.inputMode}
        value={form[f.key] ?? ""}
        onChange={set(f.key)}
        placeholder={f.placeholder}
        disabled={saving}
      />
    </div>
  );

  return (
    <Layout>
      <div className="sm-page sm-page--create sm-page--network-create">
        <section className="sm-create-hero">
          <div>
            <Link to="/networks" className="sm-page__back">
              &larr; Орқага
            </Link>
            <p className="sm-management-hero__eyebrow">Яратиш</p>
            <h1 className="sm-management-hero__title">Янги тармоқ</h1>
            <p className="sm-management-hero__text">
              Линия паспорти ва бошқарув реестри учун техник маълумотлар.
            </p>
          </div>
          <div className="sm-create-hero__chips">
            <span>Номи шарт</span>
            {isGlobal && <span>Филиал шарт</span>}
          </div>
        </section>

        {error && <div className="sm-notice sm-notice--error">{error}</div>}
        {cityError && <div className="sm-notice sm-notice--info">{cityError}</div>}

        <form className="sm-create-layout" onSubmit={handleSubmit}>
          <div className="sm-create-main">
            <section className="sm-create-section">
              <div className="sm-create-section__head">
                <span>01</span>
                <div>
                  <h2>Асосий маълумот</h2>
                  <p>Ном, филиал, кучланиш ва узунлик.</p>
                </div>
              </div>
              <div className="sm-create-grid">
                {PRIMARY_FIELDS.map(renderField)}

                {isGlobal && (
                  <div className="sm-field sm-form__full">
                    <label className="sm-field__label">Филиал *</label>
                    <select
                      className="sm-field__select"
                      value={cityId}
                      onChange={(e) => setCityId(e.target.value)}
                      disabled={saving || cityLoading}
                    >
                      <option value="">{cityLoading ? "Юкланмоқда..." : "Танланг..."}</option>
                      {cities.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </section>

            <section className="sm-create-section">
              <div className="sm-create-section__head">
                <span>02</span>
                <div>
                  <h2>Симлар ва кесишмалар</h2>
                  <p>Сим турлари, опоралар сони ва кесишма маълумотлари.</p>
                </div>
              </div>
              <div className="sm-create-grid">{TECH_FIELDS.map(renderField)}</div>
            </section>

            <section className="sm-create-section">
              <div className="sm-create-section__head">
                <span>03</span>
                <div>
                  <h2>Ўтказгич ток кўрсаткичлари</h2>
                  <p>Ўтказгичнинг рухсат этилган токи ва тармоқнинг факт максимал юкламаси.</p>
                </div>
              </div>
              <div className="sm-create-grid">{CURRENT_FIELDS.map(renderField)}</div>
            </section>
          </div>

          <aside className="sm-create-sidebar">
            <div className="sm-create-checklist">
              <h2>Ҳолат</h2>
              <span className={form.name?.trim() ? "is-done" : ""}>Номи</span>
              {isGlobal && <span className={cityId ? "is-done" : ""}>Филиал</span>}
              <span className={form.voltage ? "is-done" : ""}>Кучланиш</span>
              <span className={form.length ? "is-done" : ""}>Узунлик</span>
              <span className={form.wire_type ? "is-done" : ""}>Сим тури</span>
            </div>

            <div className="sm-create-actions">
              <button type="button" className="sm-btn sm-btn--ghost" onClick={() => navigate("/networks")} disabled={saving}>
                Бекор қилиш
              </button>
              <button type="submit" className="sm-btn sm-btn--primary" disabled={saving}>
                {saving ? "Сақланмоқда..." : "Яратиш"}
              </button>
            </div>
          </aside>
        </form>
      </div>
    </Layout>
  );
}
